import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, retry, throwError } from 'rxjs';
import { API_CALL_RETRY_COUNT, API_ENDPOINTS, ApiService } from '../api.service';
import {
  IStatisticsCleaningSchedulesResponse,
  IStatisticsDailyActivitiesResponse,
  IStatisticsIssueOverviewResponse,
  IStatisticsParkingUtilizationResponse,
  IStatisticsResponse
} from './statistics-model.interface';

@Injectable({
  providedIn: 'root'
})
export class StatisticsClientService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  private fetchRange<T>(endpoint: string, startDate: string, endDate: string): Observable<T> {
    const params = new HttpParams().set('start_date', startDate).set('end_date', endDate);
    return this.get<T>(endpoint, params);
  }

  private fetchForDate<T>(endpoint: string, date: string): Observable<T> {
    const params = new HttpParams().set('date', date);
    return this.get<T>(endpoint, params);
  }

  private get<T>(endpoint: string, params: HttpParams): Observable<T> {
    return this.http
      .get<T>(this.apiService.getUrlFor(endpoint), { params })
      .pipe(retry(API_CALL_RETRY_COUNT), catchError((error) => throwError(() => new Error(`Statistics API error: ${error.message}`))));
  }

  getCheckinRatio(startDate: string, endDate: string): Observable<IStatisticsResponse> {
    return this.fetchRange<IStatisticsResponse>(API_ENDPOINTS.statisticsCheckinRatio, startDate, endDate);
  }

  getAppUsage(startDate: string, endDate: string): Observable<IStatisticsResponse> {
    return this.fetchRange<IStatisticsResponse>(API_ENDPOINTS.statisticsAppUsage, startDate, endDate);
  }

  getStartingBookings(startDate: string, endDate: string): Observable<IStatisticsResponse> {
    return this.fetchRange<IStatisticsResponse>(API_ENDPOINTS.statisticsStartingBookings, startDate, endDate);
  }

  getFlatUtilization(startDate: string, endDate: string): Observable<IStatisticsResponse> {
    return this.fetchRange<IStatisticsResponse>(API_ENDPOINTS.statisticsFlatUtilization, startDate, endDate);
  }

  getParkingUtilization(startDate: string, endDate: string): Observable<IStatisticsParkingUtilizationResponse> {
    return this.fetchRange<IStatisticsParkingUtilizationResponse>(API_ENDPOINTS.statisticsParkingUtilization, startDate, endDate);
  }

  getCleaningSchedules(startDate: string, endDate: string): Observable<IStatisticsCleaningSchedulesResponse> {
    return this.fetchRange<IStatisticsCleaningSchedulesResponse>(API_ENDPOINTS.statisticsCleaningSchedules, startDate, endDate);
  }

  getDailyActivities(date: string): Observable<IStatisticsDailyActivitiesResponse> {
    return this.fetchForDate<IStatisticsDailyActivitiesResponse>(API_ENDPOINTS.statisticsDailyActivities, date);
  }

  getIssueOverview(startDate: string, endDate: string): Observable<IStatisticsIssueOverviewResponse> {
    return this.fetchRange<IStatisticsIssueOverviewResponse>(API_ENDPOINTS.statisticsIssueOverview, startDate, endDate);
  }
}
